<script lang="ts" context="module">
	import { page } from "$app/stores";
	import { getClassicWebUrl, getCurrentUser } from "../currentUser";
	import ToolbarItem from "./ToolbarItem.svelte";
	import ToolbarMenuItem from "./ToolbarMenuItem.svelte";
</script>

<script lang="ts">
	const CLASSIC_WEB_URL = getClassicWebUrl();
	const user = getCurrentUser();

	// TODO: Implement "integrationEditorMode" after moving the editor to the new-app (see "toolbar_button_integration_editor_mode" for reference)
	const integrationEditorMode = false;
	let showContactSalesButton = user.caps.feature__show_contact_sales_in_toolbar && !integrationEditorMode;
	let showUpgradeButton = !showContactSalesButton && !user.organization;

	$: routeId = $page.route.id;

	const dashboardUrl = user.caps.feature__new_app_dashboard ? "/?s=logo" : `${CLASSIC_WEB_URL}/app?s=logo`;
	const newslettersUrl = user.caps.feature__new_app_newsletters
		? "/newsletters?s=toolbar-button"
		: `${CLASSIC_WEB_URL}/app/my/newsletters?s=toolbar-button`;
	const mailingListsUrl = user.caps.feature__new_app_mailing_lists
		? "/lists?s=toolbar-button"
		: `${CLASSIC_WEB_URL}/app/lists/manage?s=toolbar-button`;
	const templatesUrl = user.caps.feature__new_app_templates
		? "/templates/smore?s=toolbar-button"
		: `${CLASSIC_WEB_URL}/templates/smore?s=toolbar-button`;
</script>

<ToolbarItem link={dashboardUrl} active={routeId === "/(app)"}>
	<img
		class="mr-2.5"
		src="https://cdn.smore.com/images/toolbar-logo.f046a055.svg"
		width="96"
		alt="Go to Smore home page."
		aria-label="Go to Smore home page." />
</ToolbarItem>
<span class="mx-1 hidden h-5 w-0.5 bg-[#eee] md:block"></span>
{#if !$page.data.overrideToolbarContent}
	<ToolbarItem title="Newsletters" link={newslettersUrl} active={routeId === "/(app)/newsletters/[folderId]"} visible="sm" />
	<ToolbarItem title="Mailing lists" link={mailingListsUrl} active={routeId === "/(app)/lists/[[listId]]"} visible="sm" />
	<ToolbarItem title="Templates" link={templatesUrl} active={routeId === "/(app)/templates/[category]"} visible="lg" />

	{#if showContactSalesButton}
		<ToolbarItem link="https://www.smore.com/demo/?utm_source=toolbar_button&utm_medium=website&utm_id=b2b" visible="lg">
			<div class="relative flex items-center rounded p-2.5 text-14 font-normal hover:bg-gray-100">
				Smore for Teams
				<span class="ml-2 hidden rounded bg-blue-600 px-1 py-0.5 text-12 font-bold leading-4 tracking-wider text-white xl:block"
					>Get a demo</span>
			</div>
		</ToolbarItem>
	{:else if showUpgradeButton}
		<ToolbarItem
			title="Upgrade"
			link="{CLASSIC_WEB_URL}/pricing?s=toolbar-button"
			active={routeId === "/(app)/pricing"}
			visible="lg"
			menuBehavior="opensOnHover">
			<div slot="menu">
				<div
					class="flex w-full flex-col px-[5px] text-14 [&>a>.material-icons]:font-bold
												[&>a>.material-icons]:text-nxgray-300 [&>a]:transition-all [&>a]:duration-150 [&>a]:ease-linear">
					<ToolbarMenuItem title="Educator plans" url="{CLASSIC_WEB_URL}/educators?s=toolbar-pricing-menu" />
					<ToolbarMenuItem title="Small business plans" url="{CLASSIC_WEB_URL}/business?s=toolbar-pricing-menu" />
					<ToolbarMenuItem title="Non-profit plans" url="{CLASSIC_WEB_URL}/nonprofit?s=toolbar-pricing-menu" />
					<ToolbarMenuItem
						title="Smore for Teams"
						url="https://www.smore.com/demo/?utm_source=pricing_menu&utm_medium=website&utm_id=b2b"
						subtitle="For multiple people in schools, districts, departments or teams." />
				</div>
			</div>
		</ToolbarItem>
	{/if}

	{#if user.organization}
		{@const orgScreenName = user.organization.screenName}
		{#if user.isOrgAdmin}
			{@const teamUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?s=toolbar-team-menu`}
			{@const membersUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=members&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=members&s=toolbar-team-menu`}
			{@const groupsUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=groups&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=groups&s=toolbar-team-menu`}
			{@const newslettersUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=newsletters&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=newsletters&s=toolbar-team-menu`}
			{@const reportsUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=reports&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=reports&s=toolbar-team-menu`}
			{@const domainUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=domain&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=domain&s=toolbar-team-menu`}
			{@const ssoUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=sso&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=sso&s=toolbar-team-menu`}

			<ToolbarItem
				link={teamUrl}
				active={routeId === "/(app)/organization/[idOrScreenName]"}
				visible="lg"
				title="Team"
				menuBehavior="opensOnHover">
				<div slot="menu" let:closeMenu>
					<div class="px-[13px] py-1 text-left text-13 text-white opacity-70">
						<p>Quickly access your Smore for Teams management dashboard.</p>
					</div>
					<hr class="my-[5px] h-px w-full border-t border-white opacity-15" />
					<div
						class="flex w-full flex-col px-[5px] text-14 [&>a>.material-icons]:font-bold
												[&>a>.material-icons]:text-nxgray-300 [&>a]:transition-all [&>a]:duration-150 [&>a]:ease-linear">
						<ToolbarMenuItem title="Members" url={membersUrl} on:click={closeMenu} />
						<ToolbarMenuItem title="Newsletters" url={newslettersUrl} on:click={closeMenu} />
						<ToolbarMenuItem title="Groups" url={groupsUrl} on:click={closeMenu} />
						<ToolbarMenuItem title="Reports" url={reportsUrl} on:click={closeMenu} />
						<ToolbarMenuItem title="Verified Sender Domain" url={domainUrl} on:click={closeMenu} />
						<ToolbarMenuItem title="Single Sign-On" url={ssoUrl} on:click={closeMenu} />
						{#if user.caps.brand_kit}
							<ToolbarMenuItem
								title={user.caps.brad_pitt ? "Brad Pitts" : "Brand Kits"}
								url={`/organization/${orgScreenName}?nav=brandkits&s=toolbar-button`}
								on:click={closeMenu}
								isNew={true} />
						{/if}
					</div>
				</div>
			</ToolbarItem>
		{:else if user.isGroupAdmin}
			{@const groupsUrl = user.caps.feature__new_app_management_dashboard
				? `/organization/${orgScreenName}?nav=groups&s=toolbar-button`
				: `${CLASSIC_WEB_URL}/app/organization/${orgScreenName}?nav=groups&s=toolbar-team-menu`}
			<ToolbarItem title="Team" link={groupsUrl} />
		{/if}
	{/if}
{/if}
